import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetList, Title } from 'react-admin';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import { requireAuth, requirePartner } from '@src/providers/dataProvider/shared';

import { fetchLookerEmbedUrl } from './fetchLookerEmbedUrl';
import LookerDashboardsNav from './LookerDashboardsNav';
import { toTrustedIframeSrc } from './trustedIframeSrc';
import type { ILookerDashboard } from './types';
import { DashboardResources } from '@src/types';

type LookerDashboardRecord = ILookerDashboard & { id: string };

const groupByFolder = (dashboards: LookerDashboardRecord[]): Map<string, ILookerDashboard[]> => {
  const grouped = new Map<string, ILookerDashboard[]>();
  dashboards.forEach((dashboard) => {
    const folderName = dashboard.folderName || 'Uncategorized';
    if (!grouped.has(folderName)) {
      grouped.set(folderName, []);
    }
    grouped.get(folderName)!.push(dashboard);
  });

  return grouped;
};

/**
 * Embeds the partner's Looker dashboards. Dashboards are grouped by folder in
 * a left-hand nav; selecting one loads a signed embed URL into an iframe.
 */
const LookerDashboardsPage = () => {
  const embedContainerRef = useRef<HTMLDivElement>(null);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [isEmbedLoading, setIsEmbedLoading] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);

  const { data: dashboards, isLoading, error: listError } = useGetList<LookerDashboardRecord>(
    DashboardResources.LookerDashboards,
    { pagination: { page: 1, perPage: 100 } }
  );

  const dashboardsByFolder = useMemo(() => groupByFolder(dashboards ?? []), [dashboards]);

  useEffect(() => {
    if (!selectedDashboardId || !embedContainerRef.current) {
      return;
    }

    let isMounted = true;

    const loadEmbed = async () => {
      setIsEmbedLoading(true);
      setEmbedError(null);

      try {
        const token = requireAuth();
        const partner = requirePartner();

        const signedUrl = await fetchLookerEmbedUrl({
          partnerCode: partner.code,
          dashboardId: selectedDashboardId,
          token,
        });

        if (!signedUrl) {
          throw new Error('Failed to get signed URL for dashboard.');
        }

        if (!isMounted || !embedContainerRef.current) return;

        embedContainerRef.current.innerHTML = '';
        const iframe = document.createElement('iframe');
        iframe.src = toTrustedIframeSrc(signedUrl);
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = 'none';
        iframe.allow = 'fullscreen';
        embedContainerRef.current.appendChild(iframe);

        setIsEmbedLoading(false);
      } catch (err) {
        if (!isMounted) return;
        setEmbedError(err instanceof Error ? err.message : 'Failed to load dashboard.');
        setIsEmbedLoading(false);
      }
    };

    void loadEmbed();

    return () => {
      isMounted = false;
      if (embedContainerRef.current) {
        embedContainerRef.current.innerHTML = '';
      }
    };
  }, [selectedDashboardId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (listError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {listError instanceof Error ? listError.message : 'Failed to load dashboards.'}
        </Alert>
      </Box>
    );
  }

  if (!dashboards || dashboards.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          No dashboards are available for this partner.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: 'calc(100vh - 96px)' }}>
      <Title title="Analytics" />
      <Box sx={{ display: 'flex', width: '100%', height: '100%', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Box sx={{ width: 280, minWidth: 280, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
          <LookerDashboardsNav
            dashboardsByFolder={dashboardsByFolder}
            selectedDashboardId={selectedDashboardId}
            onSelect={setSelectedDashboardId}
          />
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {!selectedDashboardId && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                Select a dashboard from the list to view it
              </Typography>
            </Box>
          )}
          {selectedDashboardId && embedError && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{embedError}</Alert>
            </Box>
          )}
          {selectedDashboardId && isEmbedLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
          <Box
            ref={embedContainerRef}
            sx={{
              width: '100%',
              height: '100%',
              display: selectedDashboardId && !isEmbedLoading && !embedError ? 'block' : 'none',
              '& iframe': { width: '100%', height: '100%', border: 'none' },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LookerDashboardsPage;
